import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './SearchableOptionsModal.module.scss';
import { BaseModal } from '@repo/components/modals';
import { ModalInput, ModalTitle } from '@repo/components/modals';
import { useIsOnScreen } from '@repo/hooks';
import { CircledXIcon } from '@repo/icons';

interface SearchableOptionsModalProps<T> {
  closeModal: () => void;
  // Must have one of options OR optionSections
  options?: T[];
  // TODO: update suggestions -> options or sectionOptions
  optionSections?: { title: string; suggestions: T[] }[];
  onOptionSelected: (option: T) => void;
  // this is run *before* the default onKeyDown handler. if it returns true, we return out of the default handler without running it.
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => boolean;
  handleSearch: (term: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  renderOption: (option: T, searchTerm?: string) => React.ReactNode;
  optionClassName?: string;
  // When set, this is rendered in place of the input field controlled by searchTerm/setSearchTerm
  alternateInput?: React.ReactNode;
  header?: React.ReactNode;
  // When set, this is rendered after the input -- e.g. for showing contact pill
  afterInput?: React.ReactNode;
  // Used to get a reference to the input node for focusing from the parent component, etc.
  setInputRef?: (ref: React.RefObject<HTMLInputElement>) => void;
  placeholder?: string;
  overlayClassName?: string;
  // When set, this is rendered in place of the options list when there are no options passed
  noOptionsMessage?: string;
  noOptionsIcon?: React.ReactNode;
  skipAutoFocus?: boolean;
  id?: string;
  // When set, this is rendered above the options list as a Title
  optionsTitle?: string;
  // When set, this is rendered below the options list but within the same scrollable container as the options
  afterOptions?: React.ReactNode;
}

interface SearchableOptionsModalRowProps {
  className: string;
  highlighted: boolean;
  onClick: () => void;
  children: React.ReactNode;
  scrollingParent: HTMLElement;
  highlightOption: () => void;
}

function SearchableOptionsModalRow({
  className,
  highlighted,
  onClick,
  children,
  scrollingParent,
  highlightOption,
}: SearchableOptionsModalRowProps) {
  const rowRef = useRef(null);
  const isInView = useIsOnScreen(rowRef, {
    root: scrollingParent,
    rootMargin: '-80px 0px -80px 0px',
  });
  // We only want to scroll when a user uses keyboard navigation. On hover/mousemove, we skipScroll on the highlight change
  const [skipScroll, setSkipScroll] = useState(false);

  useEffect(() => {
    if (highlighted && !isInView && !skipScroll) {
      // if we use "center," we don't need to worry about section titles/headers
      rowRef.current.scrollIntoView({ block: 'center' });
    }

    if (skipScroll) {
      setSkipScroll(false);
    }
  }, [highlighted]);

  return (
    <div
      className={className}
      ref={rowRef}
      onClick={onClick}
      onMouseMove={() => {
        if (!highlighted) {
          highlightOption();
          setSkipScroll(true);
        }
      }}
    >
      {children}
    </div>
  );
}

export function SearchableOptionsModal<T>({
  closeModal,
  options,
  optionSections,
  onOptionSelected,
  onInputKeyDown,
  handleSearch,
  searchTerm,
  setSearchTerm,
  renderOption,
  optionClassName,
  alternateInput,
  header,
  afterInput,
  setInputRef,
  placeholder,
  overlayClassName,
  noOptionsMessage,
  noOptionsIcon,
  skipAutoFocus,
  id,
  optionsTitle,
  afterOptions,
}: SearchableOptionsModalProps<T>) {
  const inputRef = useRef(null);
  const optionsContainerRef = useRef(null);
  const [highlightedIdx, setHighlightedIdx] = useState(0);

  const onRequestClose = (e) => {
    if (
      searchTerm.length > 0 &&
      inputRef?.current === document.activeElement &&
      e.type === 'keydown'
    ) {
      setSearchTerm('');
    } else {
      closeModal();
    }
  };

  const [optionSectionIdxOffsets, flattenedOptions] = useMemo(() => {
    if (!optionSections) {
      return [[], options];
    }

    return [
      optionSections.reduce((offsets, _, idx, sections) => {
        if (idx === 0) {
          return [0];
        }

        return [...offsets, offsets[idx - 1] + sections[idx - 1].suggestions.length];
      }, []),
      optionSections.reduce((flattened, { suggestions }) => [...flattened, ...suggestions], []),
    ];
  }, [optionSections, options]);

  const noOptions = flattenedOptions.length === 0 && !afterOptions;

  const handleKeyDown = (e) => {
    if (onInputKeyDown?.(e)) {
      return;
    }

    if (e.key === 'Enter' && flattenedOptions[highlightedIdx]) {
      onOptionSelected(flattenedOptions[highlightedIdx]);
    }

    if (e.key === 'ArrowDown' && flattenedOptions.length > 1) {
      e.preventDefault();
      if (highlightedIdx === flattenedOptions.length - 1) {
        setHighlightedIdx(0);
      } else {
        setHighlightedIdx(highlightedIdx + 1);
      }
    }

    if (e.key === 'ArrowUp' && flattenedOptions.length > 1) {
      e.preventDefault();
      if (highlightedIdx === 0) {
        setHighlightedIdx(flattenedOptions.length - 1);
      } else {
        setHighlightedIdx(highlightedIdx - 1);
      }
    }
  };

  const onSearchTermChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    handleSearch(term);
    setHighlightedIdx(0);
  };

  const renderSelectableOption = (option: T, idx: number) => {
    const isHighlighted = idx === highlightedIdx;
    return (
      <SearchableOptionsModalRow
        key={idx}
        className={cn(
          styles.option,
          optionClassName,
          isHighlighted && cn(styles.highlighted, 'highlighted'),
        )}
        highlighted={isHighlighted}
        onClick={() => {
          onOptionSelected(option);
          inputRef.current?.focus();
        }}
        scrollingParent={optionsContainerRef.current}
        highlightOption={() => setHighlightedIdx(idx)}
      >
        {renderOption(option, searchTerm)}
      </SearchableOptionsModalRow>
    );
  };

  useEffect(() => {
    setInputRef?.(inputRef);
  }, [inputRef.current]);

  return (
    <BaseModal
      isOpen={true}
      onRequestClose={onRequestClose}
      overlayClassName={overlayClassName}
      id={id}
    >
      <div className="modal-header">
        {header}
        <div className={styles.inputContainer}>
          {alternateInput ? (
            alternateInput
          ) : (
            <ModalInput
              ref={inputRef}
              value={searchTerm}
              onChange={onSearchTermChange}
              onKeyDown={handleKeyDown}
              onFocus={() => handleSearch(searchTerm)}
              placeholder={placeholder || 'Type a command or search'}
              autoFocus={!skipAutoFocus}
              className="modal-input"
            />
          )}
          {afterInput}
        </div>
      </div>
      <div
        className={cn(styles.optionsContainer, 'options-container', noOptions && styles.noOptions)}
        ref={optionsContainerRef}
      >
        {optionsTitle && <ModalTitle>{optionsTitle}</ModalTitle>}
        {noOptions && (
          <div className={cn(styles.noOptionsMessage, 'text-small')}>
            {noOptionsIcon || <CircledXIcon className={styles.inlineIcon} />}
            {noOptionsMessage || 'No Match Found'}
          </div>
        )}
        {optionSections
          ? optionSections?.map(({ title, suggestions }, sectionIdx) => (
              <React.Fragment key={sectionIdx}>
                <ModalTitle>{title}</ModalTitle>
                {suggestions.map((option, idx) =>
                  renderSelectableOption(option, idx + optionSectionIdxOffsets[sectionIdx]),
                )}
              </React.Fragment>
            ))
          : options?.map(renderSelectableOption)}
        {afterOptions}
      </div>
    </BaseModal>
  );
}
